/**
 * Layout — the app shell (design_handoff_navigation_shell): a fixed 64px domain
 * rail, one 60px chrome row carrying the scope controls, then the screen.
 *
 * The rail is position:fixed, so grid track 1 is only a SPACER — the content column
 * must be placed explicitly in track 2 or it slides underneath the rail.
 *
 * A `planned` domain (config/nav.ts) is a future feature: the shell marks it once,
 * here, and drops the surface out of colour. The pages themselves stay untouched.
 */

import React from 'react'
import { useLocation } from 'react-router-dom'
import NavRail from './NavRail'
import TopBar from './TopBar'
import Footer from '../components/Footer'
import { RAIL_CLOSED } from '../config/shell'
import { NAV_ITEMS, isNavActive } from '../config/nav'

/** The shell says a screen is a future feature, so no page has to say it itself.
 *  The strip stays in full colour; the surface under it goes grey. */
function PlannedStrip(): React.JSX.Element {
  return (
    <div className="cx-page pt-4">
      <div className="rounded-lg border border-dashed border-line bg-panel px-3 py-2 font-mono text-[10.5px] uppercase tracking-[.08em] text-mut2">
        future feature · not released
      </div>
    </div>
  )
}

export default function Layout({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { pathname } = useLocation()
  const planned = NAV_ITEMS.some((item) => item.planned && isNavActive(pathname, item))
  return (
    <div
      className="cx-scroll grid min-h-screen bg-bg font-sans text-ink"
      style={{ gridTemplateColumns: `${RAIL_CLOSED}px 1fr` }}
    >
      <NavRail />
      <div className="col-start-2 row-start-1 flex min-w-0 flex-col">
        <TopBar />
        <main className="flex-1">
          {planned && <PlannedStrip />}
          <div className={planned ? 'cx-planned' : undefined}>{children}</div>
        </main>
        <Footer />
      </div>
    </div>
  )
}
